import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

import { path } from "../data/base";
import {
  approveDisabledReason,
  buildApprovalPayload,
  buildProposalDuplicatePayload,
  canApproveProposal,
  confidenceTierChip,
  describeDecisionConsequence,
  describeMachineReason,
  describeProposalAction,
  describeProposalArea,
  describeProposalProduct,
  describeProposalStage,
  describeTargetGroupProvenance,
  isCostAvoidanceExclusion,
  isCreateCampaign,
  productsForMarket,
  proposalMatchType,
  type ApprovalDecision,
  type MachineProposal,
  type ProductOption,
  type TargetGroupOption,
} from "../data/cockpit-data";
import { marketplaceCodeLabel } from "../data/naming";
import { stageBadgeVariant, statusBadgeVariant } from "../format";
import { StatusChip } from "./StatusChip";

const MIN_REASON_LENGTH = 10;

type ExclusionMatchType = "exact" | "phrase";
type SelectedProposalAction = "exclude" | "assign";
type SubstituteAction = "ASSIGN" | "EXCLUDE" | "EXCLUDE_EXACT";

/**
 * Target groups and products are fetched ONCE per page load, not once per row.
 *
 * A review queue can hold a hundred proposals and every `tg_assignment` row
 * needs the same list; a promise cached at module scope turns a hundred
 * identical requests into one. It is deliberately not a react-query cache: the
 * lists are option catalogues for a `<select>`, not data anything renders on
 * its own, and they never need invalidating inside a session.
 */
let targetGroupsPromise: Promise<TargetGroupOption[]> | null = null;
let productsPromise: Promise<ProductOption[]> | null = null;

function loadTargetGroups(): Promise<TargetGroupOption[]> {
  if (!targetGroupsPromise) {
    targetGroupsPromise = fetch(path("/state/target-groups")).then(async (response) => {
      if (!response.ok) throw new Error("target_groups_unavailable");
      const body: unknown = await response.json();
      if (!Array.isArray(body)) throw new Error("invalid_target_groups");
      return body.filter((item): item is TargetGroupOption => {
        if (!item || typeof item !== "object") return false;
        const row = item as Record<string, unknown>;
        return (
          typeof row.tg_id === "string" &&
          typeof row.tg_name === "string" &&
          (typeof row.tg_asin === "string" || row.tg_asin === null) &&
          typeof row.status === "string"
        );
      });
    });
  }
  return targetGroupsPromise;
}

function loadProducts(): Promise<ProductOption[]> {
  if (!productsPromise) {
    productsPromise = fetch(path("/state/products")).then(async (response) => {
      if (!response.ok) throw new Error("products_unavailable");
      const body: unknown = await response.json();
      if (!Array.isArray(body)) throw new Error("invalid_products");
      return body.filter((item): item is ProductOption => {
        if (!item || typeof item !== "object") return false;
        const row = item as Record<string, unknown>;
        return (
          typeof row.asin === "string" &&
          typeof row.market === "string" &&
          (typeof row.name === "string" || row.name === null)
        );
      });
    });
  }
  return productsPromise;
}

function suggestedTargetGroupValue(
  proposal: Pick<MachineProposal, "recommended_target" | "target_group">,
  options: TargetGroupOption[],
): string {
  const matched =
    options.find((option) => option.tg_id === proposal.recommended_target) ??
    options.find((option) => option.tg_name === proposal.target_group);
  // The first option is arbitrary, not a machine suggestion. Leave the selector empty
  // until the operator chooses when the proposal carries no real target group.
  return matched?.tg_id ?? proposal.recommended_target ?? proposal.target_group ?? "";
}

async function postDecision(
  proposal_id: string,
  decision: ApprovalDecision,
  reason?: string | null,
  matchType?: ExclusionMatchType | null,
  substituteAction?: SubstituteAction | null,
  targetGroup?: string | null,
  campaignTemplate?: string | null,
  asin?: string | null,
): Promise<Response> {
  return fetch(path("/approval"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(
      buildApprovalPayload(
        proposal_id,
        decision,
        reason,
        matchType,
        substituteAction,
        targetGroup,
        campaignTemplate,
        asin,
      ),
    ),
  });
}

/** An overline label with its value — the module's fact grammar. */
function Fact({
  label,
  value,
  mono = false,
  title,
}: {
  label: string;
  value: string;
  mono?: boolean;
  title?: string;
}) {
  return (
    <span className="inline-flex items-baseline gap-1" title={title}>
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className={`text-[12.5px] font-medium text-ks-ink ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </span>
    </span>
  );
}

/** The one select style this row uses. Native, for the reason `TableControls` gives. */
const SELECT_CLASS =
  "min-h-9 max-w-full rounded-ks-sm border border-ks-grey-3 bg-ks-surface px-2 text-xs font-medium text-ks-ink";

/**
 * One proposal and its decision.
 *
 * The load-bearing rule here is that a decision is never taken blind. Three
 * separate guards enforce it, and none of them is decoration:
 *
 * * An approval that would LOSE profit is armed before it fires — inline where
 *   the impact is on screen, and by sending the reader to the Review queue
 *   where it is not.
 * * Reject and Defer require a written reason, because the audit log is the
 *   only record of why the machine's recommendation was overruled.
 * * A proposal the machine could not compute an impact for cannot be approved
 *   at all, and the row says which fact is missing.
 *
 * The colour vocabulary is gone (Meteoric painted the stage, the tier, the
 * profit pill and the risk panel). Every one of those is now a named badge:
 * this surface has thirteen tabs and a hundred rows, and a reader cannot hold
 * four colour scales in their head across it.
 */
export function ProposalRow({
  proposal,
  forceDetail = false,
  riskConfirm = false,
  showReason = true,
  compact = false,
  reasonClamped = false,
  onExpandReason,
  onDecided,
  onOpenReviewQueue,
  campaignTemplateDefault,
  campaignTemplates,
}: {
  proposal: MachineProposal;
  /** Bare-row context (Home): the impact panels are NOT visible, so a profit-risk
   *  item sends the reader to the Review queue instead of approving blind.
   *  Requires `onOpenReviewQueue` to have somewhere to send them. */
  forceDetail?: boolean;
  /** How to reach the Review queue. Passed IN rather than resolved here: a row
   *  that calls the router itself cannot render outside one, and this component
   *  is rendered in three places whose routing is not its business. */
  onOpenReviewQueue?: () => void;
  /** Full-card context (Review queue): the card IS the detail view, so a
   *  profit-risk item approves via an explicit inline confirmation. */
  riskConfirm?: boolean;
  showReason?: boolean;
  /** Compact single-line row (Home queue card). Same decision logic. */
  compact?: boolean;
  reasonClamped?: boolean;
  onExpandReason?: () => void;
  /** Fires after any successful decision so a parent queue can drop the item
   *  from a status filter without waiting for a reload. */
  onDecided?: (proposalId: string, decision: ApprovalDecision) => void;
  campaignTemplateDefault?: string | null;
  campaignTemplates?: string[] | null;
}) {
  const [status, setStatus] = useState(proposal.status);
  const [busy, setBusy] = useState<ApprovalDecision | "duplicate" | null>(null);
  const [message, setMessage] = useState<string | null>(
    proposal.status === "failed" ? "Publish failed — retry or view error." : null,
  );
  const [pendingReason, setPendingReason] = useState<Exclude<ApprovalDecision, "approved"> | null>(null);
  const [reasonText, setReasonText] = useState("");
  const [confirmingRisk, setConfirmingRisk] = useState(false);

  const profitRisk = proposal.profit_effect !== null && proposal.profit_effect < 0;
  const costAvoidance = isCostAvoidanceExclusion(proposal) && proposal.profit_effect === null;
  const machineAction: SelectedProposalAction = isCostAvoidanceExclusion(proposal) ? "exclude" : "assign";
  const [selectedAction, setSelectedAction] = useState<SelectedProposalAction>(machineAction);
  const createCampaign = isCreateCampaign(proposal);
  const [matchType, setMatchType] = useState<ExclusionMatchType>(() =>
    proposalMatchType(proposal) === "phrase" ? "phrase" : "exact",
  );
  // The breadth committed at approval, captured so the label reflects it immediately.
  const [decidedMatchType, setDecidedMatchType] = useState<ExclusionMatchType | null>(null);
  const initialTargetGroup = proposal.recommended_target ?? proposal.target_group ?? "";
  const [targetGroups, setTargetGroups] = useState<TargetGroupOption[] | null>(null);
  const [targetGroupsFailed, setTargetGroupsFailed] = useState(false);
  const [suggestedTargetGroup, setSuggestedTargetGroup] = useState(initialTargetGroup);
  const [selectedTargetGroup, setSelectedTargetGroup] = useState(initialTargetGroup);
  const [decidedTargetGroup, setDecidedTargetGroup] = useState<string | null>(null);
  const createCampaignTemplate = campaignTemplateDefault || campaignTemplates?.[0] || "";
  const [selectedCampaignTemplate, setSelectedCampaignTemplate] = useState(createCampaignTemplate);
  const [products, setProducts] = useState<ProductOption[] | null>(null);
  const [productsFailed, setProductsFailed] = useState(false);
  const [selectedAsin, setSelectedAsin] = useState(proposal.asin ?? "");
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateAsin, setDuplicateAsin] = useState("");

  // A decided proposal is terminal for this queue. `deferred` and `failed` stay
  // actionable, so they are deliberately NOT in this set.
  const terminalDecided =
    status === "approved" ||
    status === "rejected" ||
    status === "executed" ||
    status === "executing" ||
    status === "withdrawn";

  const substitutionAvailable = proposal.action_type === "tg_assignment" && !terminalDecided;
  const substituted = substitutionAvailable && selectedAction !== machineAction;
  const substituteAction: SubstituteAction | null = substituted
    ? selectedAction === "assign"
      ? "ASSIGN"
      : "EXCLUDE_EXACT"
    : null;
  // tg_assignment exclusions commit to the BQ exclusion list, so the operator can
  // widen/narrow the breadth at approval time. A substitution encodes its breadth
  // in substitute_action and must never travel with match_type.
  const matchTypeSelectable =
    proposal.action_type === "tg_assignment" &&
    isCostAvoidanceExclusion(proposal) &&
    !substituted;
  const assignment = proposal.action_type === "tg_assignment" && selectedAction === "assign";
  const tgSelectable = assignment || createCampaign;

  const templateSelectorVisible = createCampaign && !terminalDecided && (campaignTemplates?.length ?? 0) >= 2;
  const marketProducts = productsForMarket(products ?? [], proposal.market);

  useEffect(() => {
    if (!tgSelectable || terminalDecided) return;
    let active = true;
    loadTargetGroups()
      .then((options) => {
        if (!active) return;
        const suggested = suggestedTargetGroupValue(proposal, options);
        setTargetGroups(options);
        setTargetGroupsFailed(false);
        setSuggestedTargetGroup(suggested);
        setSelectedTargetGroup(suggested);
      })
      .catch(() => {
        if (active) setTargetGroupsFailed(true);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.recommended_target, proposal.target_group, terminalDecided, tgSelectable, createCampaign]);

  useEffect(() => {
    if (!createCampaign || terminalDecided) return;
    let active = true;
    loadProducts()
      .then((options) => {
        if (!active) return;
        setProducts(options);
        setProductsFailed(false);
        const available = productsForMarket(options, proposal.market);
        setDuplicateAsin(available.find((product) => product.asin !== proposal.asin)?.asin ?? "");
      })
      .catch(() => {
        if (active) setProductsFailed(true);
      });
    return () => {
      active = false;
    };
  }, [createCampaign, proposal.asin, proposal.market, terminalDecided]);

  useEffect(() => {
    setSelectedCampaignTemplate(createCampaignTemplate);
  }, [createCampaignTemplate]);

  const targetGroupSelectorVisible =
    tgSelectable &&
    !terminalDecided &&
    targetGroups !== null &&
    (targetGroups.length > 0 || suggestedTargetGroup !== "");
  const suggestedTargetGroupInList =
    targetGroups?.some((option) => option.tg_id === suggestedTargetGroup) ?? false;
  const approvalContext = { selectedTargetGroup };
  const effectiveProposal = substituted
    ? { ...proposal, recommended_action: substituteAction ?? proposal.recommended_action }
    : proposal;
  const ready = canApproveProposal(effectiveProposal, approvalContext);
  const helperDisabledReason = approveDisabledReason(effectiveProposal, approvalContext);
  const disabledReason =
    assignment && targetGroupsFailed
      ? "Target groups are unavailable. Reload to try again."
      : assignment && selectedTargetGroup === "" && compact
        ? "Select a target group in the review queue."
        : helperDisabledReason;
  const canInlineApprove = ready && !(assignment && targetGroupsFailed) && !profitRisk && !forceDetail;

  // The keyword is the single most important field on a proposal, so it headlines
  // the row. Proposals that carry none (objective changes) fall back to the target.
  const headline = proposal.keyword ?? proposal.recommended_target ?? proposal.recommended_action;
  // While the match selector is live the label carries NO breadth parenthetical: the
  // selector is the single source of the breadth, so a static "(exact match)" beside a
  // Phrase selection would contradict it.
  const selectedTargetGroupLabel =
    targetGroups?.find((option) => option.tg_id === selectedTargetGroup)?.tg_name ??
    decidedTargetGroup ??
    proposal.target_group ??
    proposal.recommended_target;
  const actionLabel = assignment
    ? selectedTargetGroupLabel
      ? `Add to target group: ${selectedTargetGroupLabel}`
      : "Add to target group"
    : matchTypeSelectable
    ? decidedMatchType
      ? `Exclude keyword (${decidedMatchType} match)`
      : terminalDecided
        ? describeProposalAction(proposal)
        : "Exclude keyword"
    : selectedAction === "exclude" && proposal.action_type === "tg_assignment"
      ? "Exclude keyword (exact match)"
      : describeProposalAction(proposal);
  const stage = describeProposalStage(proposal);
  const area = describeProposalArea(proposal);
  const explainedReason = describeMachineReason(proposal.reason);
  const decisionConsequence = describeDecisionConsequence(proposal);
  const reasonTextContent = explainedReason
    ? `${explainedReason.purpose ? `${explainedReason.purpose}: ` : ""}${explainedReason.summary}`
    : null;
  const reasonTitle = explainedReason
    ? [
        explainedReason.summary,
        ...(decisionConsequence
          ? [`Approve: ${decisionConsequence.approve}`, `Reject: ${decisionConsequence.reject}`]
          : []),
      ].join("\n\n")
    : undefined;
  const productSelectorVisible = createCampaign && !terminalDecided && products !== null;
  const selectedProductInList = marketProducts.some((product) => product.asin === selectedAsin);
  const provenance = describeTargetGroupProvenance(proposal.target_group_provenance);
  const tierChip = confidenceTierChip(proposal.confidence_tier);

  async function decide(decision: ApprovalDecision, reason?: string) {
    if (decision === "approved" && !ready) return;
    setBusy(decision);
    setMessage(null);
    const previous = status;
    try {
      const response = await postDecision(
        proposal.proposal_id,
        decision,
        reason,
        decision === "approved" && matchTypeSelectable ? matchType : null,
        decision === "approved" ? substituteAction : null,
        decision === "approved" &&
          targetGroupSelectorVisible &&
          selectedTargetGroup !== "" &&
          (substituted || assignment || selectedTargetGroup !== suggestedTargetGroup)
          ? selectedTargetGroup
          : null,
        decision === "approved" &&
          templateSelectorVisible &&
          selectedCampaignTemplate !== createCampaignTemplate
          ? selectedCampaignTemplate
          : null,
        decision === "approved" && productSelectorVisible && selectedAsin !== (proposal.asin ?? "")
          ? selectedAsin
          : null,
      );
      if (response.status === 403) {
        setMessage("You don't have go-live authority.");
        setStatus(previous);
        return;
      }
      if (!response.ok) {
        const responseBody = (await response.json().catch(() => null)) as { error?: string } | null;
        setMessage(
          decision === "approved" && responseBody?.error === "no_margin_for_asin"
            ? "No margin data for that product, so the impact cannot be forecast. Pick another product."
            : decision === "approved"
              ? "Publish failed — retry or view error."
              : "Decision failed. Retry.",
        );
        setStatus(previous);
        return;
      }
      setStatus(decision);
      if (decision === "approved" && matchTypeSelectable) setDecidedMatchType(matchType);
      if (decision === "approved" && assignment) {
        const selectedOption = targetGroups?.find((option) => option.tg_id === selectedTargetGroup);
        setDecidedTargetGroup(
          selectedOption?.tg_name ??
            proposal.target_group ??
            selectedTargetGroup ??
            proposal.recommended_target,
        );
      }
      setPendingReason(null);
      setReasonText("");
      setConfirmingRisk(false);
      // The StatusChip now carries the outcome, so no redundant "Approved." line.
      setMessage(null);
      onDecided?.(proposal.proposal_id, decision);
    } finally {
      setBusy(null);
    }
  }

  async function duplicateProposal() {
    const payload = buildProposalDuplicatePayload(
      proposal.proposal_id,
      duplicateAsin,
      null,
      selectedCampaignTemplate,
      selectedTargetGroup,
    );
    if (!payload) return;
    setBusy("duplicate");
    setMessage(null);
    try {
      const response = await fetch(path("/proposal-duplicate"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const responseBody = (await response.json().catch(() => null)) as {
        error?: string;
        idempotent?: boolean;
      } | null;
      if (!response.ok) {
        const errorMessages: Record<string, string> = {
          no_margin_for_asin:
            "No margin data for that product, so the impact cannot be forecast. Pick another product.",
          duplicate_is_source: "That is this proposal's own product. Pick a different one.",
          unknown_asin: "That product is not active in this marketplace.",
          cross_market_duplicate_unsupported: `A campaign in another marketplace needs its own proposal. This duplicates within ${proposal.market ?? "this market"} only.`,
        };
        setMessage(errorMessages[responseBody?.error ?? ""] ?? "Duplicate failed. Retry.");
        return;
      }
      const product = marketProducts.find((option) => option.asin === duplicateAsin);
      setMessage(
        `Created a proposal for ${product?.name || duplicateAsin}.${responseBody?.idempotent ? " That proposal already exists." : ""}`,
      );
      setDuplicateOpen(false);
    } finally {
      setBusy(null);
    }
  }

  const reasonTooShort = reasonText.trim().length < MIN_REASON_LENGTH;

  // Terminal states compress to one history line: outcome + headline + effect.
  if (terminalDecided) {
    return (
      <div className="flex min-h-11 flex-wrap items-center gap-2.5 py-1 text-[13.5px]">
        <StatusChip status={status} />
        <span className="min-w-0 font-medium text-ks-ink">{headline}</span>
        {actionLabel && <span className="text-xs text-muted-foreground">· {actionLabel}</span>}
        <span className="flex-1" />
        {proposal.units_effect && (
          <span className="text-xs tabular-nums text-muted-foreground">{proposal.units_effect}</span>
        )}
      </div>
    );
  }

  const approveControl = canInlineApprove ? (
    <Button onClick={() => decide("approved")} disabled={busy !== null || pendingReason !== null}>
      {busy === "approved" ? "Approving…" : "Approve"}
    </Button>
  ) : ready && profitRisk && riskConfirm ? (
    // Armed, not fired. The word "profit risk" is on the button itself, because a
    // reader who has to hover to learn what a colour meant has already clicked.
    <Button
      variant="outline"
      aria-pressed={confirmingRisk}
      onClick={() => setConfirmingRisk((value) => !value)}
      disabled={busy !== null || pendingReason !== null}
    >
      Approve (profit risk)
    </Button>
  ) : ready && forceDetail && onOpenReviewQueue ? (
    <Button
      variant="outline"
      onClick={onOpenReviewQueue}
      title="Profit-risk approvals confirm in the review queue, where the full impact is visible."
    >
      Open in review queue
    </Button>
  ) : (
    <Button
      variant="outline"
      aria-disabled="true"
      title={disabledReason ?? "Not approvable yet."}
      onClick={(event) => event.preventDefault()}
    >
      Approve disabled
    </Button>
  );

  const deferButton = (
    <Button
      variant="outline"
      aria-pressed={pendingReason === "deferred"}
      onClick={() => {
        setConfirmingRisk(false);
        setPendingReason(pendingReason === "deferred" ? null : "deferred");
      }}
      disabled={busy !== null}
    >
      Defer
    </Button>
  );

  const duplicateButton = createCampaign ? (
    <Button
      variant="outline"
      aria-pressed={duplicateOpen}
      onClick={() => {
        setConfirmingRisk(false);
        setPendingReason(null);
        setDuplicateOpen((value) => !value);
      }}
      disabled={busy !== null || productsFailed}
      title={productsFailed ? "Products are unavailable." : undefined}
    >
      Duplicate
    </Button>
  ) : null;

  const flows = (
    <>
      {confirmingRisk && (
        <div className="mt-3 rounded-lg border border-ks-grey-3 bg-muted/40 px-3.5 py-3">
          <p className="text-[13.5px] font-semibold text-ks-ink">
            Profit-yield effect is{" "}
            {proposal.profit_effect?.toLocaleString("en-US", { maximumFractionDigits: 2 }) ?? "unknown"}.
            Review the impact, then confirm.
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Button onClick={() => decide("approved")} disabled={busy !== null}>
              {busy === "approved" ? "Approving…" : "Confirm approve"}
            </Button>
            <Button variant="outline" onClick={() => setConfirmingRisk(false)} disabled={busy !== null}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {pendingReason && (
        <div className="mt-3 rounded-lg border border-ks-grey-3 bg-muted/40 px-3.5 py-3">
          <label className="block text-[13px] font-semibold text-ks-ink" htmlFor={`reason-${proposal.proposal_id}`}>
            {pendingReason === "rejected" ? "Why reject this proposal?" : "Why defer this proposal?"}{" "}
            <span className="text-xs font-normal text-muted-foreground">
              (min {MIN_REASON_LENGTH} characters, saved to the audit log)
            </span>
          </label>
          <Textarea
            id={`reason-${proposal.proposal_id}`}
            className="mt-2 min-h-16"
            value={reasonText}
            onChange={(event) => setReasonText(event.target.value)}
            placeholder={
              pendingReason === "rejected"
                ? "e.g. duplicate of an existing exact-match campaign"
                : "e.g. hold until Q3 budget confirmed"
            }
            disabled={busy !== null}
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => decide(pendingReason, reasonText.trim())}
              disabled={busy !== null || reasonTooShort}
            >
              {busy === pendingReason
                ? "Saving…"
                : pendingReason === "rejected"
                  ? "Confirm reject"
                  : "Confirm defer"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPendingReason(null);
                setReasonText("");
              }}
              disabled={busy !== null}
            >
              Cancel
            </Button>
            {reasonText.trim().length > 0 && reasonTooShort && (
              <span className="text-xs text-muted-foreground">
                Need at least {MIN_REASON_LENGTH} characters.
              </span>
            )}
          </div>
        </div>
      )}
      {duplicateOpen && (
        <div className="mt-3 rounded-lg border border-ks-grey-3 bg-muted/40 px-3.5 py-3">
          <label
            className="block text-[13px] font-semibold text-ks-ink"
            htmlFor={`duplicate-product-${proposal.proposal_id}`}
          >
            Duplicate for product
          </label>
          <select
            id={`duplicate-product-${proposal.proposal_id}`}
            className={`mt-2 ${SELECT_CLASS}`}
            value={duplicateAsin}
            onChange={(event) => setDuplicateAsin(event.target.value)}
            disabled={busy !== null}
          >
            <option value="" disabled>
              Select a sibling product
            </option>
            {marketProducts.map((product) => (
              <option key={product.asin} value={product.asin}>
                {product.name ? `${product.name} (${product.asin})` : product.asin}
              </option>
            ))}
          </select>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Button onClick={duplicateProposal} disabled={busy !== null || !duplicateAsin}>
              {busy === "duplicate" ? "Creating…" : "Create duplicate"}
            </Button>
            <Button variant="outline" onClick={() => setDuplicateOpen(false)} disabled={busy !== null}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {!ready && disabledReason && (
        <p className="mt-2 text-xs font-medium text-muted-foreground">{disabledReason}</p>
      )}
      {message && (
        <p className="mt-2 text-xs text-muted-foreground" role="status">
          {message}
        </p>
      )}
    </>
  );

  // Compact variant (Home queue card): one row per proposal. Reject stays in the
  // review queue; Defer keeps its full reason flow below.
  if (compact) {
    return (
      <div className={`border-t border-ks-grey-3 py-1.5 first:border-t-0 ${busy ? "opacity-60" : ""}`}>
        <div className="flex min-h-9 flex-wrap items-center gap-2.5">
          {area && <Badge variant="outline">{area}</Badge>}
          <Badge variant={stageBadgeVariant(stage.tone)}>{stage.label}</Badge>
          <div className="min-w-0 flex-1 truncate text-[13.5px]">
            <span className="font-medium text-ks-ink">{headline}</span>
            {actionLabel && <span className="text-muted-foreground"> · {actionLabel}</span>}
          </div>
          {proposal.units_effect && (
            <span className="text-xs font-medium tabular-nums text-ks-ink">{proposal.units_effect}</span>
          )}
          {tierChip && <Badge variant={statusBadgeVariant(tierChip.tone)}>{tierChip.label}</Badge>}
          {deferButton}
          {approveControl}
        </div>
        {flows}
      </div>
    );
  }

  return (
    <article className={busy ? "opacity-60" : undefined}>
      {/* Identity first, impact second. Area leads because it is the widest bucket
          the proposal sits in, so it reads before the stage within it. */}
      <div className="flex flex-wrap items-center gap-2">
        {area && <Badge variant="outline">{area}</Badge>}
        <Badge variant={stageBadgeVariant(stage.tone)}>{stage.label}</Badge>
        {actionLabel && <Badge variant="default">{actionLabel}</Badge>}
        <span className="flex-1" />
        <Badge variant="outline" className="tabular-nums">
          {proposal.units_effect ?? "Impact pending"}
        </Badge>
        {/* Named in words, not painted. "profit risk" is the one that changes a
            decision, and it must survive greyscale and a pasted screenshot. */}
        <Badge
          variant={profitRisk ? "destructive" : costAvoidance || proposal.profit_effect === null ? "outline" : "secondary"}
          className="tabular-nums"
        >
          {costAvoidance
            ? "cost-avoidance"
            : proposal.profit_effect === null
              ? "not computed"
              : profitRisk
                ? "profit risk"
                : "profit ok"}
        </Badge>
      </div>
      {/* Workflow state, demoted from the row above: it says where the proposal is
          in the process, not what it is about. */}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <StatusChip status={status} />
        {tierChip && <Badge variant={statusBadgeVariant(tierChip.tone)}>{tierChip.label}</Badge>}
      </div>
      <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-[15px] font-semibold leading-tight text-ks-ink">{headline}</h3>
          <div className="mt-2 flex flex-wrap gap-x-3.5 gap-y-1">
            {proposal.market && (
              <Fact
                label="Market"
                value={marketplaceCodeLabel(proposal.market)}
                title={
                  createCampaign
                    ? "A campaign in another marketplace needs its own proposal, because the demand forecast is measured per marketplace."
                    : undefined
                }
              />
            )}
            {proposal.asin && !productSelectorVisible && (
              <Fact
                label={proposal.product ? "Product" : "ASIN"}
                value={describeProposalProduct(proposal) ?? proposal.asin}
                mono={!proposal.product}
              />
            )}
            {proposal.target_group && !targetGroupSelectorVisible && (
              <span className="inline-flex items-center gap-2">
                <Fact label="Target group" value={proposal.target_group} />
                {provenance && (
                  <Badge variant={proposal.target_group_provenance === "assigned" ? "secondary" : "outline"}>
                    {provenance}
                  </Badge>
                )}
              </span>
            )}
            {proposal.recommended_target && <Fact label="Target" value={proposal.recommended_target} />}
            {createCampaign && !templateSelectorVisible && (
              <Fact
                label="Campaign template"
                value={createCampaignTemplate}
                title="Default template. Selecting a different template per campaign is not available yet."
              />
            )}
          </div>
          {showReason &&
            reasonTextContent &&
            (reasonClamped ? (
              <div className="mt-2 flex max-w-[72ch] items-baseline gap-2 text-[13px] text-muted-foreground" title={reasonTitle}>
                <span className="min-w-0 flex-1 truncate">{reasonTextContent}</span>
                {onExpandReason && (
                  <Button variant="link" size="xs" className="flex-none" onClick={onExpandReason}>
                    more
                  </Button>
                )}
              </div>
            ) : (
              <p className="mt-2 max-w-[72ch] text-[13px] text-muted-foreground" title={reasonTitle}>
                {reasonTextContent}
              </p>
            ))}
          {substitutionAvailable && (
            <fieldset className="mt-2.5">
              <legend className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Action to approve
              </legend>
              <RadioGroup
                className="mt-2 flex flex-wrap gap-4"
                value={selectedAction}
                onValueChange={(value) => setSelectedAction(value as SelectedProposalAction)}
                disabled={busy !== null}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={`action-exclude-${proposal.proposal_id}`} value="exclude" />
                  <Label htmlFor={`action-exclude-${proposal.proposal_id}`}>Exclude</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={`action-assign-${proposal.proposal_id}`} value="assign" />
                  <Label htmlFor={`action-assign-${proposal.proposal_id}`}>Add to target group</Label>
                </div>
              </RadioGroup>
              {substituted && (
                <p className="mt-2 max-w-[72ch] text-xs text-muted-foreground">
                  The profit forecast on this card was computed for the machine&apos;s action, not this
                  choice. This change takes effect on the next preview, not instantly.
                </p>
              )}
            </fieldset>
          )}
          {templateSelectorVisible && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <label
                htmlFor={`campaign-template-${proposal.proposal_id}`}
                className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Campaign template
              </label>
              <select
                id={`campaign-template-${proposal.proposal_id}`}
                className={SELECT_CLASS}
                value={selectedCampaignTemplate}
                onChange={(event) => setSelectedCampaignTemplate(event.target.value)}
                disabled={busy !== null}
              >
                {campaignTemplates?.map((template) => (
                  <option key={template} value={template}>
                    {template}
                  </option>
                ))}
              </select>
            </div>
          )}
          {matchTypeSelectable && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <span
                id={`match-type-label-${proposal.proposal_id}`}
                className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Exclude as
              </span>
              <div role="group" aria-labelledby={`match-type-label-${proposal.proposal_id}`} className="flex gap-1.5">
                {(["exact", "phrase"] as const).map((option) => (
                  <Button
                    key={option}
                    size="sm"
                    variant={matchType === option ? "default" : "outline"}
                    aria-pressed={matchType === option}
                    title={
                      option === "exact"
                        ? "Exclude only this exact search term."
                        : "Exclude every search containing this term."
                    }
                    onClick={() => setMatchType(option)}
                    disabled={busy !== null}
                  >
                    {option === "exact" ? "Exact match" : "Phrase match"}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {targetGroupSelectorVisible && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <label
                htmlFor={`target-group-${proposal.proposal_id}`}
                className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {createCampaign ? "Target group" : "Assign to"}
              </label>
              <select
                id={`target-group-${proposal.proposal_id}`}
                className={SELECT_CLASS}
                value={selectedTargetGroup}
                onChange={(event) => setSelectedTargetGroup(event.target.value)}
                disabled={busy !== null}
              >
                {selectedTargetGroup === "" && (
                  <option value="" disabled>
                    Select a target group
                  </option>
                )}
                {!suggestedTargetGroupInList && suggestedTargetGroup && (
                  <option value={suggestedTargetGroup} disabled>
                    {proposal.target_group ?? proposal.recommended_target ?? suggestedTargetGroup} (suggested,
                    archived)
                  </option>
                )}
                {targetGroups?.map((option) => (
                  <option key={option.tg_id} value={option.tg_id}>
                    {option.tg_name}
                    {option.tg_id === suggestedTargetGroup ? " (suggested)" : ""}
                  </option>
                ))}
              </select>
              {provenance && (
                <Badge variant={proposal.target_group_provenance === "assigned" ? "secondary" : "outline"}>
                  {provenance}
                </Badge>
              )}
            </div>
          )}
          {productSelectorVisible && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <label
                htmlFor={`product-${proposal.proposal_id}`}
                className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Product
              </label>
              <select
                id={`product-${proposal.proposal_id}`}
                className={SELECT_CLASS}
                value={selectedAsin}
                onChange={(event) => setSelectedAsin(event.target.value)}
                disabled={busy !== null}
              >
                {!selectedProductInList && selectedAsin && (
                  <option value={selectedAsin} disabled>
                    {selectedAsin} (current)
                  </option>
                )}
                {marketProducts.map((product) => (
                  <option key={product.asin} value={product.asin}>
                    {product.name ? `${product.name} (${product.asin})` : product.asin}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {/* The decision cluster is always visible. Approve is the only primary
            button on the card. */}
        <div className="flex flex-none flex-wrap items-start gap-2">
          {approveControl}
          <Button
            variant="outline"
            aria-pressed={pendingReason === "rejected"}
            onClick={() => {
              setConfirmingRisk(false);
              setPendingReason(pendingReason === "rejected" ? null : "rejected");
            }}
            disabled={busy !== null}
          >
            Reject
          </Button>
          {deferButton}
          {duplicateButton}
        </div>
      </div>
      {flows}
    </article>
  );
}
